/** Schichtplan (E10.3): Wochenansicht je Person, Schichten und Abwesenheiten, Druckansicht. Kasse zeigt „heute eingeteilt“ zuerst. */
import { useState } from "react";
import { Button, Dialog, Notice, Seitenzustand, TextField } from "../ui";
import { Card, FormGrid, PageHeader, Select } from "../ui/Layout";
import { ApiFehler } from "../api/client";
import { formatTag, tagVon } from "@shared/zeit";
import { tagPlus } from "@shared/perioden";
import { useSchichtAktion, useSchichten } from "./alltag-api";

const WT = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function montag(tag: string): string {
  const d = new Date(`${tag}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d.toISOString().slice(0, 10);
}

export function Schichtplan() {
  const heute = tagVon();
  const [von, setVon] = useState(montag(heute));
  const q = useSchichten(von);
  const aktion = useSchichtAktion();
  const [dialog, setDialog] = useState<{ benutzerId: string; tag: string } | null>(null);
  const [form, setForm] = useState({ von: "10:00", bis: "18:00", art: "schicht" as "schicht" | "abwesend", notiz: "" });
  const [meldung, setMeldung] = useState<string | null>(null);
  const tage = Array.from({ length: 7 }, (_, i) => tagPlus(von, i));

  return (
    <>
      <PageHeader kicker="Personal" title="Schichtplan" sub="Wer ist wann im Laden – die Kasse schlägt die eingeteilte Person zuerst vor" actions={<span className="sc-toolbar app-nicht-drucken"><Button variant="quiet" icon="chevron-left" aria-label="Vorige Woche" onClick={() => setVon(tagPlus(von, -7))} /><Button variant="quiet" onClick={() => setVon(montag(heute))}>Diese Woche</Button><Button variant="quiet" icon="chevron-right" aria-label="Nächste Woche" onClick={() => setVon(tagPlus(von, 7))} /><Button variant="secondary" icon="printer" onClick={() => window.print()}>Drucken</Button></span>} />
      {meldung ? <Notice tone="error">{meldung}</Notice> : null}
      <Seitenzustand query={q} lines={6}>
        {(d) => (
          <Card title={`Woche ${formatTag(von)} – ${formatTag(tagPlus(von, 6))}`} flush className="app-druck">
            <div className="app-tabellen-scroll">
              <table className="rg-table app-schichtplan">
                <thead>
                  <tr>
                    <th>Person</th>
                    {tage.map((t, i) => <th key={t} className={t === heute ? "is-heute" : undefined}>{WT[i]}<div className="sc-muted" style={{ fontWeight: 400 }}>{t.slice(8, 10)}.{t.slice(5, 7)}.</div></th>)}
                  </tr>
                </thead>
                <tbody>
                  {d.personen.map((p) => (
                    <tr key={p.id}>
                      <td><span className="sc-strong">{p.name}</span><div className="sc-muted sc-small">{p.rolle}</div></td>
                      {tage.map((t) => {
                        const eintraege = d.schichten.filter((s) => s.benutzerId === p.id && s.tag === t);
                        return (
                          <td key={t} className={t === heute ? "is-heute" : undefined}>
                            <div className="sc-col" style={{ gap: 4 }}>
                              {eintraege.map((s) => (
                                <button key={s.id} type="button" className={`app-schicht${s.art === "abwesend" ? " is-abwesend" : ""}`} title={s.notiz || (s.art === "abwesend" ? "Abwesend" : "Schicht")} onClick={() => confirm(`${s.art === "abwesend" ? "Abwesenheit" : "Schicht"} ${s.von}–${s.bis} entfernen?`) && aktion.mutate({ art: "loeschen", id: s.id })}>
                                  {s.art === "abwesend" ? "frei" : `${s.von}–${s.bis}`}
                                </button>
                              ))}
                              <button type="button" className="app-schicht app-schicht--neu app-nicht-drucken" aria-label={`Schicht für ${p.name} am ${formatTag(t)}`} onClick={() => { setDialog({ benutzerId: p.id, tag: t }); setMeldung(null); }}>+</button>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="sc-muted sc-small app-nicht-drucken" style={{ padding: "var(--space-3) var(--space-4)" }}>Antippen zum Entfernen, + für eine neue Schicht oder Abwesenheit. Planungsdaten, kein Geld – Löschen ist erlaubt.</p>
          </Card>
        )}
      </Seitenzustand>
      <Dialog open={!!dialog} title={dialog ? `${formatTag(dialog.tag)}: ${q.data?.personen.find((p) => p.id === dialog.benutzerId)?.name ?? ""}` : ""} size="sm" onClose={() => setDialog(null)} actions={<><Button variant="quiet" onClick={() => setDialog(null)}>Abbrechen</Button><Button variant="primary" loading={aktion.isPending} onClick={() => dialog && aktion.mutate({ art: "anlegen", daten: { benutzerId: dialog.benutzerId, tag: dialog.tag, von: form.von, bis: form.bis, art: form.art, notiz: form.notiz } }, { onSuccess: () => setDialog(null), onError: (e) => setMeldung(e instanceof ApiFehler ? e.message : "Das hat nicht geklappt.") })}>Eintragen</Button></>}>
        <div className="app-form">
          <div className="sc-toolbar" role="radiogroup" aria-label="Art">
            <button type="button" className="sc-pill" aria-pressed={form.art === "schicht"} onClick={() => setForm({ ...form, art: "schicht" })}>Schicht</button>
            <button type="button" className="sc-pill" aria-pressed={form.art === "abwesend"} onClick={() => setForm({ ...form, art: "abwesend" })}>Abwesend (Urlaub, krank)</button>
          </div>
          {form.art === "schicht" ? (
            <FormGrid>
              <TextField label="Von" type="time" value={form.von} onChange={(e) => setForm({ ...form, von: e.target.value })} />
              <TextField label="Bis" type="time" value={form.bis} onChange={(e) => setForm({ ...form, bis: e.target.value })} />
            </FormGrid>
          ) : null}
          <TextField label="Notiz" value={form.notiz} onChange={(e) => setForm({ ...form, notiz: e.target.value })} maxLength={120} />
          <Select label="Wiederholen" value="nein" onChange={() => undefined} hint="Wiederholungen kommen später – bis dahin je Tag eintragen"><option value="nein">Nur dieser Tag</option></Select>
        </div>
      </Dialog>
    </>
  );
}
